<template>
  <section class="section-headline">
    <div class="section-headline-inner">
      <div class="headline-inner">
        <h1 v-if="campaignsPageData" class="bcrumbs">
          <span class="bcrumb"
                v-for="(breadcrumb, index) in breadcrumbs" :key="breadcrumb.Title" @click="onBreadcrumbClick(breadcrumb)">
            <span v-if="index > 0">> </span>{{ breadcrumb.Title }}
          </span>
        </h1>
        <mq-layout class="headline-controls" mq="lg">
          <div class="headline-switches">
            <b-field>
              <b-switch :value="!showGraphs" @input="onShowGraphsChange($event)">{{ $t("Campaigns.HideGraphs") }}</b-switch>
            </b-field>
          </div>

          <div class="headline-actions">
            <b-button type="is-link" class="is-icon" tag="a" v-if="campaignsPageData" href="#" @click="emitToggleAddAccount($event)">
              <icon-base width="10" height="10" view-box="0 0 10 10" icon-name="plus"><icon-plus /></icon-base>
              <span>{{ $t("Campaigns.AddAdAccount.Title") }}</span>
            </b-button>
            <b-button type="is-primary" class="is-icon" tag="a" v-if="campaignsPageData" :href="`${campaignCreateRouteValue}?IsMediaChannel=${campaignsPageData.IsMediaChannel}`">
              <icon-base width="10" height="10" view-box="0 0 10 10" icon-name="plus"><icon-plus /></icon-base>
              <span>{{ $t("Campaigns.Add") }}</span>
            </b-button>
          </div>
        </mq-layout>

        <mq-layout class="headline-filters-toggler" :mq="['sm', 'md']">
          <button type="button" class="is-transparent button-text-w" @click="showMobileFilters">
            <icon-base width="22.22" height="20" view-box="0 0 22.22 20" icon-name="user-search"><icon-user-search /></icon-base>
            <span class="text">{{ $t("Filters") }}</span>
          </button>
        </mq-layout>
      </div>

      <mq-layout class="headline-controls-m" :mq="['sm', 'md']">
        <div class="headline-actions">
          <b-button type="is-link" class="is-icon" size="is-small" @click="emitToggleAddAccount($event)">
            <icon-base width="10" height="10" view-box="0 0 10 10" icon-name="plus"><icon-plus /></icon-base>
            <span>{{ $t("Campaigns.AddAdAccount.Title") }}</span>
          </b-button>
          <b-button type="is-primary" class="is-icon" size="is-small" tag="a" v-if="campaignsPageData" :href="`${campaignCreateRouteValue}?IsMediaChannel=${campaignsPageData.IsMediaChannel}`">
            <icon-base width="10" height="10" view-box="0 0 10 10" icon-name="plus"><icon-plus /></icon-base>
            <span>{{ $t("Campaigns.Add") }}</span>
          </b-button>
        </div>

        <div class="headline-switches">
          <b-field>
            <b-switch :value="!showGraphs" @input="onShowGraphsChange($event)">{{ $t("Campaigns.HideGraphs") }}</b-switch>
          </b-field>
        </div>
      </mq-layout>
    </div>
    <mq-layout :mq="['sm', 'md']">
      <transition name="fade">
        <div v-show="mobileFilters" class="headline-filters-m" :class="{ active: mobileFilters }">
          <div class="headline-filters-m-container">
            <div class="headline-filters-m-h">
              <div class="headline-filters-m-title">
                <icon-base width="22.22" height="20" view-box="0 0 22.22 20" icon-name="user-search"><icon-user-search /></icon-base>
                <span class="text">{{ $t("Filters") }}</span>
              </div>
              <div class="headline-filters-m-close" @click="hideMobileFilters">
                <icon-base width="16" height="16" view-box="0 0 7 7" icon-name="times"><icon-times /></icon-base>
              </div>
            </div>
            <div class="headline-filters-m-b">
              <div class="headline-filters-m-filters">
                <CampaignsFiltersDropdowns />
                <CampaignsFiltersDate />

                <div class="input-w">
                  <b-field class="search-container">
                    <icon-base width="16" height="16" view-box="0 0 16 16" icon-name="search"><icon-search /></icon-base>
                    <b-input :placeholder="$t('Grid.Search')" @blur="onSearchChange($event)" @keyup.native.enter="onSearchChange($event)"></b-input>
                  </b-field>
                </div>
              </div>
              <div class="buttons">
                <b-button type="is-primary" expanded @click="onFilterClick()">{{ $t("Filter") }}</b-button>
              </div>
            </div>
          </div>
          <div class="headline-filters-m-bg"></div>
        </div>
      </transition>
    </mq-layout>
  </section>
</template>

<script lang="ts">
import VueMq from "vue-mq";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { campaignCreateRoute } from "@/router";
import { Breadcrumb, BreadcrumbAction } from "@/models/Breadcrumb";
import { CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import CampaignsFiltersDate from "@/components/Campaigns/CampaignsFiltersDate.vue";
import CampaignsFiltersDropdowns from "@/components/Campaigns/CampaignsFiltersDropdowns.vue";
import { RefreshGridMode } from "@/models/campaigns/CampaignGridView";
import IconBase from "../IconBase.vue";
import IconCaretLeft from "../icons/IconCaretLeft.vue";
import IconPlus from "../icons/IconPlus.vue";
import IconSearch from "../icons/IconSearch.vue";
import IconTimes from "../icons/IconTimes.vue";
import IconUserSearch from "../icons/IconUserSearch.vue";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});
const namespace = "campaignsModule";

@Component({
  components: {
    CampaignsFiltersDropdowns,
    CampaignsFiltersDate,
    IconBase,
    IconPlus,
    IconUserSearch,
    IconTimes,
    IconSearch,
    IconCaretLeft,
  },
})
export default class CampaignsHeadline extends Vue {

  @Prop() readonly page!: string;
  @Prop() readonly breadcrumbs!: Breadcrumb[];

  //#region Vuex getters and actions

  @Getter("campaignsPageData", { namespace }) campaignsPageData!: CampaignsPageViewModel;
  @Getter("showGraphs", { namespace }) showGraphs!: boolean;
  @Action("setNeedToRefreshCampaignsGrid", { namespace }) setNeedToRefreshCampaignsGrid!: (needToRefreshLeadsGrid: RefreshGridMode) => void;
  @Action("setSearchValue", { namespace }) setSearchValue!: (searchValue: string) => void;
  @Action("setShowGraphs", { namespace }) setShowGraphs!: (showGraphs: boolean) => void;

  //#endregion Vuex getters and actions


  //#region Getters

  get campaignCreateRouteValue(): string {
    return campaignCreateRoute;
  }

  //#endregion Getters


  //#region Handlers

  emitToggleAddAccount(e: Event): void {
    e.preventDefault();
    this.$emit("toggle-add-ad-account");
  }

  public onFilterClick(): void {
    this.setNeedToRefreshCampaignsGrid(RefreshGridMode.Full);
    this.hideMobileFilters();
  }

  onSearchChange(e: Event) {
    this.setSearchValue((e.target as HTMLInputElement).value);
  }

  public onShowGraphsChange(hideGraphs: boolean): void {
    this.setShowGraphs(!hideGraphs);
  }

  //#endregion Handlers


  public mobileFilters = false;

  public showMobileFilters(): void {
    this.mobileFilters = true;
    this.popupBodyOn();
  }

  public hideMobileFilters(): void {
    this.mobileFilters = false;
    this.popupBodyOff();
  }
  
  public onBreadcrumbClick(breadcrumb: Breadcrumb) {
    if (breadcrumb.Action === BreadcrumbAction.Action) {
      if (breadcrumb.Link === "campaign") {
        this.$emit("ads-close");
      } else if (breadcrumb.Link === "campaigns") {
        this.$emit("adgroups-close");
      }
    }
  }

  public popupBodyOn(): void {
    document.body.style.top = "-" + window.scrollY + "px";
    document.body.style.position = "fixed";
  }

  public popupBodyOff(): void {
    const scrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo(0, parseInt(scrollY || "0") * -1);
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.section-headline {
  .headline-inner {
    display: flex;
    align-items: center;
    padding: 16px 0;
    border-bottom: 1px solid #d3d6e2;

    @media (max-width: 991px) {
      height: 52px;
    }

    .bcrumbs {
      .bcrumb {
        cursor: pointer;
      }
    }
  }

  h1 {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    margin-bottom: 3px;
    @media (max-width: 991px) {
      font-size: 20px;
    }
  }
  .crumbs {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 3px;
    @media (max-width: 991px) {
      font-size: 20px;
    }
    .crumbs-parent {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }
    .crumb {
      a {
        text-decoration: underline;
        color: $dark;
        &:hover {
          color: $primary;
        }
      }
    }
    .icon-w {
      margin: 2px 13px 0;
      color: rgba($dark, 0.6);
      @media (max-width: 991px) {
        margin: 2px 8px 0;
      }
    }
  }

  .headline-controls {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    font-size: 16px;

    align-items: center;
    margin-right: auto;

    .headline-switches {
      .field {
        margin: 3px 18px 3px 0;
        display: flex;
      }
      .switch {
      }
      .check {
        .control-label {
          white-space: nowrap;
        }
      }
    }
    .headline-actions {
      margin-right: 30px;
      .button {
        height: 36px;
        &:not(:last-child) {
          margin-left: 15px;
        }
      }
    }
  }
  .headline-controls-m {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    flex-wrap: wrap;
    font-size: 16px;
    align-items: center;
    @media (max-width: 359px) {
      // display: block;
    }
    .headline-switches {
      // margin-right: 30px;
      .field {
        margin: 3px 0 3px 0;
        display: flex;
      }
      .switch {
      }
      .check {
        .control-label {
          white-space: nowrap;
        }
      }
    }
    .headline-actions {
      .button {
        border-radius: 6px;
        height: 30px;
        margin-left: 13px;
      }
    }
  }
  .headline-filters-toggler {
    margin-right: auto;
    margin-left: -5px;

    @media (min-width: 992px) {
      display: none;
    }
    .button-text-w {
      transition: all 0.2s;
      &:hover {
        color: $primary;
      }
      .icon-w + .text {
        margin-right: 7px;
      }
    }
  }

  .headline-switches-m {
    display: flex;
    flex-wrap: wrap;
    font-size: 16px;
    align-items: center;
    padding: 10px 0;
    justify-content: flex-start;
    @media (max-width: 399px) {
      justify-content: space-between;
    }
    @media (max-width: 359px) {
      display: block;
    }
    .field {
      margin: 5px 0 5px 0;
      display: flex;
      position: relative;
    }
    .border {
      display: block;
      width: 1px;
      height: 16px;
      background: $lightd;
      margin: 0 30px;
      @media (max-width: 399px) {
        margin: 0 10px;
      }
      @media (max-width: 359px) {
        display: none;
      }
    }
    .switch {
      direction: ltr;
      @media (max-width: 359px) {
        width: 100%;
        justify-content: space-between;
      }
    }
    .check {
      direction: rtl;
      .control-label {
        white-space: nowrap;
      }
    }
  }

  .headline-filters-m {
    position: fixed;
    z-index: -1;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    .headline-filters-m-container {
      position: fixed;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      z-index: 10;
      transition: all 0.5s;
      transform: translateX(0);
      background: #f6f7fb;
    }
    .headline-filters-m-h {
      position: fixed;
      top: 0;
      left: 0;
      height: 56px;
      width: 100%;
      color: #fff;
      display: flex;
      align-items: center;
      padding: 12px 15px 13px 10px;
      background: linear-gradient(90deg, $primary 0%, #1ca8d9 52.75%);
      .headline-filters-m-close {
        padding: 5px;
        cursor: pointer;
        line-height: 0;
        margin-right: auto;
        transition: all 0.2s;
        &:hover {
          opacity: 0.8;
        }
        .icon-w {
          svg {
          }
        }
      }
      .headline-filters-m-title {
        display: flex;
        align-items: center;
        font-size: 21px;
        .icon-w {
          margin-left: 10px;
        }
      }
    }
    .headline-filters-m-b {
      position: fixed;
      top: 56px;
      bottom: 0;
      left: 0;
      width: 100%;
      padding: 0 15px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      .headline-filters-m-switches {
        padding: 20px 0;
        border-bottom: 1px solid $lightd;
        .switch {
          width: 100%;
          justify-content: space-between;
        }
      }
      .headline-filters-m-filters {
        padding: 20px 0 0;
        display: flex;
        flex-wrap: wrap;
        margin: 0 -8px;
        .dropdown-w {
          padding: 0 8px 15px;
          .dropdown {
            width: 100%;
            .dropdown-trigger {
              width: 100%;
              .button {
                width: 100%;
              }
            }
          }
          &.datepicker-w {
            width: 100%;
            flex: 0 0 100%;
            .datepicker-w-i {
              position: relative;
            }
            .dropdown {
              width: 100%;
              .dropdown-trigger {
                width: 100%;
                .control {
                  width: 100%;
                }
              }
            }
          }
        }
        .input-w {
          padding: 0 8px 15px;
          width: 100%;
          .field.has-addons {
          }
        }
      }
      .buttons {
        margin: auto 0 0;
      }
    }
    .headline-filters-m-bg {
      position: fixed;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      z-index: 9;
      transition: all 0.5s;
      background: rgba(0, 0, 0, 0.3);
    }
    .buttons {
      padding-bottom: 30px;
      .button {
        margin: 0;
      }
    }

    &.fade-enter-active,
    &.fade-leave-active {
      z-index: 101;
      transition: all 0.5s;
    }
    &.fade-enter,
    &.fade-leave-to,
    &.fade-leave-active {
      opacity: 1;
      .headline-filters-m-container {
        transform: translateX(-100%);
      }
      .headline-filters-m-bg {
        opacity: 0;
      }
    }
    &.fade-enter {
    }
    &.active {
      z-index: 101;
      .headline-filters-m-container {
        // transform: translateX(0);
      }
    }
  }
}
html[dir="ltr"] {
  .section-headline {
    .headline-controls {
      margin: 0 0 0 auto;
      .headline-switches {
      }
      .headline-actions {
        margin: 0 0 0 30px;
        .button:not(:last-child) {
          margin: 0 15px 0 0;
        }
      }
    }
    .headline-controls-m {
      .headline-switches {
        // margin: 0 0 0 30px;
        .field {
          margin: 3px 0 3px 18px;
        }
      }
      .headline-actions {
        .button {
          margin: 0 13px 0 0;
        }
      }
    }
    .headline-filters-toggler {
      margin: 0 -5px 0 auto;
      .button-text-w .icon-w + .text {
        margin: 0 0 0 7px;
      }
    }
    .headline-filters-m {
      .headline-filters-m-h {
        .headline-filters-m-close {
          margin: 0 0 0 auto;
        }
      }
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";
.section-headline {
  .headline-filters-m {
    .headline-filters-m-b {
      .headline-filters-m-filters {
        .dropdown-w {
          @media (max-width: 575px) {
            width: 100%;
            flex: 0 0 100%;
          }
        }
        .input-w {
          padding: 0 8px 15px;
          width: 100%;
          .field.has-addons {
            position: relative;
            display: block;
            .icon-w {
              position: absolute;
              top: 50%;
              right: 8px;
              transform: translateY(-50%);
              color: $dark;
              z-index: 5;
            }
            .input {
              padding: 5px 32px 5px 10px;
              border-radius: 4px;
            }
          }
        }
      }
    }
  }
}

html[dir="ltr"] {
  .section-headline {
    .headline-switches {
      margin: 0 0 0 auto;
      .field {
        margin: 3px 0 3px 18px;
      }
    }
  }
}
</style>
